import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Search, ChevronDown, Printer, Plus } from "lucide-react";
import "../../style/transfertopos.css";
import "../../style/transferDetail.css";
import "../../style/batches.css";
import RedDeleteIcon from "../../assets/icons/RedDeleteIcon.js";
import TodoListIcon from "../../assets/icons/TodoListIcon.js";
import BackIcon from "../../assets/icons/BackIcon.js";
import NoDataIcon from "../../assets/icons/NoDataIcon.js";
import DeleteModalIcon from "../../assets/delete-modal-icon.svg";
import TransferPosModal from "../../assets/transfer-pos-modal.svg";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { stockTransferAPI } from "../../services";
import { productAPI } from "../../services";
import { tagAPI } from "../../services";
import transferVector from "../../assets/transfer-vector.svg";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const normalizeTransferDetail = (payload) => {
  if (!payload) {
    return {
      transferNumber: "-",
      isTransferred: false,
      summary: {
        totalModels: 0,
        totalQuantity: 0,
      },
      items: [],
    };
  }

  const transferNumber =
    payload.transfer_number ??
    payload.transferNumber ??
    payload.transfer?.transfer_number ??
    payload.transfer ??
    "-";

  const isTransferred = payload.is_transfered ?? payload.isTransferred ?? false;

  const summary = {
    totalModels:
      payload.total_products ??
      payload.summary?.total_products ??
      0,
    totalQuantity:
      payload.total_quantity ??
      payload.summary?.total_quantity ??
      0,
  };

  const rawItems =
    payload.items ??
    payload.data ??
    payload.results ??
    payload.transfers ??
    [];

  const normalizedItems = Array.isArray(rawItems)
    ? rawItems.map((item, index) => ({
      id: item.id ?? `transfer-item-${index}`,
      model: item.product_name ?? "-",
      tag: item.unique_identifier_identifier ?? "-",
      category: item.product_brand ?? "-",
      sellingPrice: typeof item.selling_price === "number" ? item.selling_price : 0,
      quantity: typeof item.quantity === "number" ? item.quantity : 0,
      raw: item,
    }))
    : [];

  return {
    transferNumber,
    isTransferred,
    summary,
    items: normalizedItems,
  };
};

export default function TransferDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { isPartsURL, isAccessoriesURL } = useSelector((state) => state.domain);
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(() =>
    normalizeTransferDetail(null)
  );
  const [isAddStockModalOpen, setIsAddStockModalOpen] = useState(false);
  const [isAddTagModalOpen, setIsAddTagModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [pendingDeleteItem, setPendingDeleteItem] = useState(null);
  const [isTransferConfirmModalOpen, setIsTransferConfirmModalOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [didLoadOnce, setDidLoadOnce] = useState(false);
  const [isDeletingItem, setIsDeletingItem] = useState(false);
  const [, setIsSearching] = useState(false);

  const variantSuffix = isPartsURL ? "-parts" : isAccessoriesURL ? "-acc" : "";
  const brandColor = isPartsURL
    ? "#DB2E31"
    : isAccessoriesURL
      ? "#72BF48"
      : "#2F80ED";
  const backLinkClass = [
    "transfer-detail-backlink",
    isPartsURL
      ? "transfer-detail-backlink-parts"
      : isAccessoriesURL
        ? "transfer-detail-backlink-acc"
        : ""
  ]
    .filter(Boolean)
    .join(" ");

  const filteredItems = items;

  const handlePrint = () => {
    if (items.length === 0) {
      toast.warning("No items to print");
      return;
    }

    const doc = new jsPDF();
    
    doc.setFontSize(18);
    doc.text("Transfer to POS", 14, 20);
    
    doc.setFontSize(11);
    doc.text(`Transfer Number: ${detail.transferNumber}`, 14, 30);
    doc.text(`Date: ${new Date().toLocaleDateString()}`, 14, 36);
    
    const tableData = items.map(item => [
      item.model,
      item.tag,
      item.category,
      item.quantity,
      typeof item.sellingPrice === "number" ? `€${item.sellingPrice.toFixed(2)}` : "-"
    ]);

    autoTable(doc, {
      startY: 42,
      head: [["Model", "Tag", "Category", "Qty", "Selling Price"]],
      body: tableData,
      theme: "grid",
      headStyles: { fillColor: brandColor, textColor: 255 },
      styles: { fontSize: 9 },
      columnStyles: {
        1: { halign: 'center' },
        3: { halign: 'center' },
        4: { halign: 'center' }
      },
      didParseCell: function(data) {
        if (data.section === 'head' && [1, 3, 4].includes(data.column.index)) {
          data.cell.styles.halign = 'center';
        }
      }
    });

    doc.save(`Transfer_${detail.transferNumber}_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success("PDF generated successfully");
  };

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const fetchDetail = async () => {
      if (!id) return;
      setIsLoading(true);

      try {
        const response = await stockTransferAPI.getTransferSummary(id, {
          signal: controller.signal,
        });

        if (!isMounted) return;

        const payload = response.data || response;
        const normalized = normalizeTransferDetail(payload);
        setDetail(normalized);
        setItems(normalized.items);
        setDidLoadOnce(true);
      } catch (error) {
        if (!isMounted || error.name === "CanceledError") {
          return;
        }
        console.error("Failed to load transfer detail:", error);
        const message =
          error?.response?.data?.message ||
          error?.response?.data?.detail ||
          error?.message ||
          "Failed to load transfer detail";
        toast.error(message);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchDetail();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [id]);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();
    
    if (!search.trim()) return;
    
    const timeoutId = setTimeout(async () => {
      if (!id) return;
      
      setIsSearching(true);
      try {
        const response = await stockTransferAPI.getTransferSummary(id, {
          params: { search: search.trim() },
          signal: controller.signal,
        });

        if (!isMounted) return;

        const payload = response.data || response;
        const normalized = normalizeTransferDetail(payload);
        setItems(normalized.items);
      } catch (error) {
        if (!isMounted || error.name === "CanceledError") return;
        console.error("Search failed:", error);
      } finally {
        if (isMounted) setIsSearching(false);
      }
    }, 300);

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [search, id]);

  const refreshTransferData = async () => {
    try {
      const response = await stockTransferAPI.getTransferSummary(id);
      const payload = response.data || response;
      const normalized = normalizeTransferDetail(payload);
      setDetail(normalized);
      setItems(normalized.items);
    } catch (error) {
      console.error("Failed to refresh transfer data:", error);
    }
  };

  const handleDeleteClick = (item) => {
    setPendingDeleteItem(item);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!pendingDeleteItem || isDeletingItem) return;

    const targetId = pendingDeleteItem.raw?.id ?? pendingDeleteItem.id;
    if (!targetId) {
      toast.error("Could not determine item id");
      setPendingDeleteItem(null);
      setIsDeleteModalOpen(false);
      return;
    }

    setIsDeletingItem(true);
    try {
      await stockTransferAPI.deleteTransferItem(targetId);
      toast.success("Transfer item deleted successfully");
      
      setDetail(prev => ({
        ...prev,
        summary: {
          ...prev.summary,
          totalModels: Math.max(0, prev.summary.totalModels - 1)
        }
      }));
      
      setItems((prev) =>
        prev.filter((entry) => {
          const entryId = entry.raw?.id ?? entry.id;
          return entryId !== targetId;
        })
      );
      setIsDeleteModalOpen(false);
      setPendingDeleteItem(null);
    } catch (error) {
      console.error("Failed to delete transfer item:", error);
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.message ||
        "Failed to delete transfer item";
      toast.error(message);
    } finally {
      setIsDeletingItem(false);
    }
  };

  return (
    <div className="transfer-detail-page">
      <div className="transfer-detail-shell">
        <button
          type="button"
          className={backLinkClass}
          onClick={() => navigate("/transfer_to_pos")}
        >
          <BackIcon fill={brandColor} />
          <span>Back to Transfer to POS</span>
        </button>

        <section className="transfer-detail-card">
          <div className="transfer-detail-header">
            <div className="transfer-detail-heading">
              <h1>Transfer to POS</h1>
              <p>Move stock from godown to POS for retail or bulk selling</p>
            </div>
            <div className="transfer-detail-header-actions">
              <button
                type="button"
                className="transfer-detail-print-btn"
                style={{ color: brandColor, borderColor: brandColor }}
                onClick={handlePrint}
              >
                <Printer size={16} strokeWidth={1.6} color={brandColor} />
                <span>Print</span>
              </button>
              <button
                type="button"
                className={`transfer-detail-primary-btn${variantSuffix}`}
                onClick={() => setIsTransferConfirmModalOpen(true)}
                disabled={detail.isTransferred}
                style={detail.isTransferred ? { opacity: 0.5, cursor: "not-allowed" } : {}}
              >
                <img src={transferVector} alt="Transfer" style={{ width: "13.75px", height: "13.75px", filter: "brightness(0) invert(1)" }} />
                <span>Transfer to POS</span>
              </button>
            </div>
          </div>

          <div className="transfer-detail-stats">
            <div className="transfer-detail-stat-card">
              <div>
                <p className="transfer-detail-stat-label">Total Models</p>
                <p className="transfer-detail-stat-value">
                  {detail.summary.totalModels.toLocaleString()}
                </p>
              </div>
              <div className="transfer-detail-stat-icon">
                <TodoListIcon fill={brandColor} />
              </div>
            </div>
          </div>

          <div className="transfer-detail-toolbar">
            <div className="transfer-toolbar">
              <div
                className={`transfer-detail-search ${isPartsURL
                    ? "search-box-parts"
                    : isAccessoriesURL
                      ? "search-box-acc"
                      : "search-box"
                  }`}
              >
                <span className="icon" aria-hidden>
                  <Search size={16} color="#8C959F" />
                </span>
                <input
                  type="text"
                  placeholder="Search by product name, brand, or IMEI..."
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </div>
            </div>
            <div className="transfer-detail-toolbar-buttons">
              <button
                type="button"
                className="transfer-detail-tag-btn"
                onClick={() => setIsAddTagModalOpen(true)}
                disabled={detail.isTransferred}
                style={detail.isTransferred ? { opacity: 0.5, cursor: "not-allowed" } : {}}
              >
                <Plus size={16} /> Add by Tag
              </button>
              <button
                type="button"
                className={`transfer-detail-stock-btn${variantSuffix}`}
                onClick={() => setIsAddStockModalOpen(true)}
                disabled={detail.isTransferred}
                style={detail.isTransferred ? { opacity: 0.5, cursor: "not-allowed" } : {}}
              >
                <Plus size={16} /> Add Stock
              </button>
            </div>
          </div>

          <div className="transfer-detail-table-card">
            <table className="transfer-detail-table">
              <thead>
                <tr>
                  <th>MODEL</th>
                  <th>TAG</th>
                  <th>CATEGORY</th>
                  <th>SELLING PRICE</th>
                  <th>QUANTITY</th>
                  <th className="action-header">ACTION</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan="6">
                      <div className="transfer-detail-empty">
                        <p>Loading transfer items...</p>
                      </div>
                    </td>
                  </tr>
                ) : filteredItems.length === 0 ? (
                  <tr>
                    <td colSpan="6">
                      <div className="transfer-detail-empty">
                        <NoDataIcon />
                        <p>{didLoadOnce ? "No matching records found" : "No records available"}</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredItems.map((item) => (
                    <tr key={item.id}>
                      <td>{item.model}</td>
                      <td>{item.tag}</td>
                      <td>{item.category}</td>
                      <td>
                        {typeof item.sellingPrice === "number"
                          ? item.sellingPrice.toLocaleString(undefined, {
                            style: "currency",
                            currency: "EUR",
                          })
                          : item.sellingPrice}
                      </td>
                      <td>
                        {typeof item.quantity === "number"
                          ? item.quantity.toLocaleString()
                          : item.quantity}
                      </td>
                      <td className="action-buttons">
                        <button
                          type="button"
                          className="action-btn delete"
                          title="Delete"
                          onClick={() => handleDeleteClick(item)}
                          disabled={detail.isTransferred}
                          style={detail.isTransferred ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                        >
                          <RedDeleteIcon fill="currentColor" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
      <AddStockModal
        open={isAddStockModalOpen}
        onClose={() => setIsAddStockModalOpen(false)}
        primaryColor={brandColor}
        onSuccess={refreshTransferData}
      />
      <AddTagModal
        open={isAddTagModalOpen}
        onClose={() => setIsAddTagModalOpen(false)}
        primaryColor={brandColor}
        onSuccess={refreshTransferData}
      />
      <DeleteConfirmModal
        open={isDeleteModalOpen}
        item={pendingDeleteItem}
        onCancel={() => {
          setPendingDeleteItem(null);
          setIsDeleteModalOpen(false);
        }}
        onConfirm={handleDeleteConfirm}
        isDeleting={isDeletingItem}
      />
      <TransferConfirmModal
        open={isTransferConfirmModalOpen}
        onClose={() => setIsTransferConfirmModalOpen(false)}
        primaryColor={brandColor}
      />
    </div>
  );
}

function AddStockModal({ open, onClose, primaryColor, onSuccess }) {
  const { id } = useParams();
  const { isPartsURL, isAccessoriesURL, isGodownURL } = useSelector((state) => state.domain);
  const searchInputRef = useRef(null);
  const [formData, setFormData] = useState({
    productId: "",
    sellingPrice: "",
    quantity: "",
  });
  const [errors, setErrors] = useState({});
  const [isProductDropdownOpen, setIsProductDropdownOpen] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [products, setProducts] = useState([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);

  useEffect(() => {
    if (open) {
      setFormData({
        productId: "",
        sellingPrice: "",
        quantity: "",
      });
      setSelectedProduct(null);
      setErrors({});
      setProductSearchTerm("");
      setIsProductDropdownOpen(false);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    
    let isMounted = true;
    const controller = new AbortController();

    const fetchProducts = async () => {
      if (!productSearchTerm.trim()) {
        if (products.length === 0) {
          setIsLoadingProducts(true);
          try {
            const response = await productAPI.getProductsList({ page: 1, page_size: 100, only_tag: 0 });
            if (isMounted) setProducts(response.results || response.data || []);
          } catch (error) {
            if (isMounted && error.name !== "CanceledError") toast.error("Failed to load products");
          } finally {
            if (isMounted) setIsLoadingProducts(false);
          }
        }
        return;
      }

      setIsLoadingProducts(true);
      try {
        const response = await productAPI.getProductsList({ search: productSearchTerm, page: 1, page_size: 100, only_tag: 0 });
        if (isMounted) setProducts(response.results || response.data || []);
      } catch (error) {
        if (isMounted && error.name !== "CanceledError") toast.error("Failed to search products");
      } finally {
        if (isMounted) setIsLoadingProducts(false);
      }
    };

    const timeoutId = setTimeout(fetchProducts, 300);

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [productSearchTerm, open]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isProductDropdownOpen &&
        !event.target.closest(".stock-product-dropdown-container")
      ) {
        setIsProductDropdownOpen(false);
      }
    };

    if (isProductDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isProductDropdownOpen]);

  if (!open) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleProductSelect = (product) => {
    setFormData((prev) => ({ 
      ...prev, 
      productId: product.id,
      sellingPrice: product.latest_selling_price || product.avg_selling_price || 0
    }));
    setSelectedProduct(product);
    setIsProductDropdownOpen(false);
    setProductSearchTerm("");
    if (errors.productId) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.productId;
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.productId) newErrors.productId = "Product is required";
    if (!formData.quantity || parseInt(formData.quantity) <= 0)
      newErrors.quantity = "Quantity must be greater than 0";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await stockTransferAPI.addTransferItem({
        transfer_id: parseInt(id),
        product_id: parseInt(formData.productId),
        quantity: parseInt(formData.quantity),
      });
      toast.success("Stock added successfully");
      onClose();
      if (onSuccess) await onSuccess();
    } catch (error) {
      console.error("Failed to add stock:", error);
      const errorData = error?.response?.data;
      const message = errorData?.errors?.quantity || 
        errorData?.message ||
        errorData?.detail ||
        error?.message ||
        "Failed to add stock";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="stock-modal-overlay"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="stock-modal transfer-stock-modal noscrollbar" role="dialog" aria-modal="true">
        <div className="stock-modal-header">
          <div className="stock-modal-title-section">
            <h2 className="stock-modal-title">Add Products to Transfer</h2>
            <p className="stock-modal-subtitle">
            Fill in the details below to add products for transfer to POS
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="stock-modal-form">
          <div className="stock-modal-body">
            <div className="stock-form-group">
              <label className="stock-form-label">Product *</label>
              <div className="stock-product-dropdown-container">
                <div
                  className={`${isPartsURL
                      ? "dropdown-input-parts"
                      : isAccessoriesURL
                        ? "dropdown-input-acc"
                        : isGodownURL
                          ? "dropdown-input-godown"
                          : "dropdown-input"
                    } ${errors.productId ? "is-invalid" : ""}`}
                  onClick={() => setIsProductDropdownOpen(!isProductDropdownOpen)}
                >
                  <span
                    className={
                      selectedProduct ? "selected-value" : "not-selected-value"
                    }
                  >
                    {selectedProduct
                      ? selectedProduct.name
                      : "Select product"}
                  </span>
                  <ChevronDown className="dropdown-arrow" size={16} />
                </div>

                {isProductDropdownOpen && (
                  <div className="dropdown-menu">
                    <div className="dropdown-search">
                      <Search className="batches-search-icon" size={16} />
                      <input
                        ref={searchInputRef}
                        type="text"
                        className={`${isPartsURL
                            ? "batches-search-input-parts"
                            : isAccessoriesURL
                              ? "batches-search-input-acc"
                              : isGodownURL
                                ? "batches-search-input-godown"
                                : "batches-search-input"
                          }`}
                        placeholder="Search product..."
                        value={productSearchTerm}
                        onChange={(e) => setProductSearchTerm(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="dropdown-list noscrollbar">
                      {isLoadingProducts ? (
                        <div className="batches-dropdown-item no-results">
                          Loading products...
                        </div>
                      ) : products.length > 0 ? (
                        products.map((product) => (
                          <div
                            key={product.id}
                            className="batches-dropdown-item"
                            onClick={() => handleProductSelect(product)}
                          >
                            <div>
                              <div>{product.name}</div>
                              <div style={{ fontSize: "0.85em", color: "#666", marginTop: "2px" }}>
                                {[
                                  product.ram,
                                  product.storage,
                                  product.variants?.map(v => Object.values(v).join(", ")).join(", "),
                                  product.suffix,
                                  product.used_mobile !== undefined && product.used_mobile !== null ? (product.used_mobile === false ? "New" : "Used") : null
                                ].filter(Boolean).join(" | ") || `Available: ${product.in_stock || 0}`}
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="batches-dropdown-item no-results">
                          No products found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {errors.productId && (
                <div className="stock-form-error">{errors.productId}</div>
              )}
            </div>

            <div style={{ display: "flex", gap: "16px" }}>
              <div className="stock-form-group" style={{ flex: 1 }}>
                <label className="stock-form-label">Selling Price</label>
                <input
                  type="number"
                  name="sellingPrice"
                  className={`${isPartsURL
                      ? "stock-form-input-parts"
                      : isAccessoriesURL
                        ? "stock-form-input-acc"
                      : isGodownURL
                        ? "stock-form-input-godown"
                        : "stock-form-input"
                    }`}
                  value={formData.sellingPrice}
                  readOnly
                  disabled
                />
              </div>

              <div className="stock-form-group" style={{ flex: 1 }}>
                <label className="stock-form-label">Quantity *</label>
                <input
                  type="number"
                  name="quantity"
                  className={`${isPartsURL
                      ? "stock-form-input-parts"
                      : isAccessoriesURL
                        ? "stock-form-input-acc"
                      : isGodownURL
                        ? "stock-form-input-godown"
                        : "stock-form-input"
                    } ${errors.quantity ? "error" : ""}`}
                  value={formData.quantity}
                  onChange={handleChange}
                  min="1"
                />
                {errors.quantity && (
                  <div className="stock-form-error">{errors.quantity}</div>
                )}
              </div>
            </div>
          </div>

          <div className="stock-modal-footer">
            <button
              type="button"
              className="stock-cancel-btn"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`${isPartsURL
                  ? "stock-submit-btn-parts"
                  : isAccessoriesURL
                    ? "stock-submit-btn-acc"
                    : isGodownURL
                      ? "stock-submit-btn-godown"
                      : "stock-submit-btn"
                }`}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Adding..." : "Add Stock"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DeleteConfirmModal({ open, item, onCancel, onConfirm, isDeleting }) {
  if (!open || !item) return null;
  // const purchaseLabel = item.tag || item.model || `#${item.id}`;

  return (
    <div
      className="modal-overlay"
      onClick={(event) => {
        if (event.target === event.currentTarget) onCancel();
      }}
    >
      <div
        className="delete-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="deletePurchaseTitle"
      >
        <div className="modal-icon">
          <img src={DeleteModalIcon} alt="Delete warning" />
        </div>
        <h3 className="modal-title" id="deletePurchaseTitle">
          Delete Transfer
        </h3>
        <p className="modal-message">
          Are you sure you want to delete this transfer? This action cannot be undone.
        </p>
        <div className="modal-actions">
          <button type="button" className="modal-cancel-btn" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="modal-delete-btn"
            onClick={onConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

function TransferConfirmModal({ open, onClose, primaryColor }) {
  const navigate = useNavigate();
  const { id } = useParams();
  // const { isPartsURL, isAccessoriesURL } = useSelector((state) => state.domain);
  const [isConfirming, setIsConfirming] = useState(false);

  if (!open) return null;

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      await stockTransferAPI.publishTransfer(id);
      toast.success("Transfer confirmed successfully");
      onClose();
      navigate("/transfer_to_pos");
    } catch (error) {
      console.error("Failed to confirm transfer:", error);
      const errorData = error?.response?.data;
      const message = errorData?.errors?.items ||
        errorData?.errors?.target_schema ||
        errorData?.message ||
        errorData?.detail ||
        error?.message ||
        "Failed to confirm transfer";
      toast.error(message);
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <div
      className="modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="transfer-confirm-modal" role="dialog" aria-modal="true">
        <div className="transfer-confirm-icon">
          <img src={TransferPosModal} alt="Transfer POS Modal" />
        </div>
        <h3 className="transfer-confirm-title">Confirmation</h3>
        <p className="transfer-confirm-message">
          Are you sure you want to Transfer this all products to the POS?
        </p>
        <div className="transfer-confirm-actions">
          <button
            type="button"
            className="transfer-confirm-cancel-btn"
            onClick={onClose}
            disabled={isConfirming}
          >
            No, Back to Lists
          </button>
          <button
            type="button"
            className="transfer-confirm-submit-btn"
            style={{ background: primaryColor }}
            onClick={handleConfirm}
            disabled={isConfirming}
          >
            {isConfirming ? "Confirming..." : "Confirm Transfers"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AddTagModal({ open, onClose, primaryColor, onSuccess }) {
  const { id } = useParams()
  const dropdownRef = useRef(null);
  const tagSearchInputRef = useRef(null);
  const [tagDropdownOpen, setTagDropdownOpen] = useState(false);
  const [tagSearchQuery, setTagSearchQuery] = useState("");
  const [tags, setTags] = useState([]);
  const [isLoadingTags, setIsLoadingTags] = useState(false);
  const [selectedTagId, setSelectedTagId] = useState(null);
  const [tagProducts, setTagProducts] = useState([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formState, setFormState] = useState({
    selectedTag: "",
    quantity: "",
    applyToAll: true,
  });

  useEffect(() => {
    if (open) {
      setFormState({
        selectedTag: "",
        quantity: "",
        applyToAll: true,
      });
      setSelectedTagId(null);
      setTagProducts([]);
      setTagSearchQuery("");
      setTagDropdownOpen(false);
      setTags([]);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    if (!tagDropdownOpen) return;
    
    // Auto-focus the search input when dropdown opens
    setTimeout(() => {
      tagSearchInputRef.current?.focus();
    }, 0);
    
    const handleClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setTagDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
    };
  }, [tagDropdownOpen]);

  const fetchTags = async (searchQuery = "") => {
    setIsLoadingTags(true);
    try {
      const params = { page: 1, page_size: 100 };
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }
      const response = await tagAPI.getTagsList(params);
      setTags(response.results || response.data || []);
    } catch (error) {
      console.error("Failed to fetch tags:", error);
      toast.error("Failed to load tags");
    } finally {
      setIsLoadingTags(false);
    }
  };

  const fetchTagProducts = async (tagId) => {
    setIsLoadingProducts(true);
    try {
      const response = await tagAPI.getProductsByTag(tagId);
      const products = response.results || response.data || response.products || [];
      setTagProducts(products.map(p => ({
        ...p,
        quantity: formState.quantity || 0,
        buying_price: p.latest_buying_price || p.avg_buying_price || 0,
        selling_price: p.latest_selling_price || p.avg_selling_price || 0,
      })));
    } catch (error) {
      console.error("Failed to fetch tag products:", error);
      toast.error("Failed to load tag products");
    } finally {
      setIsLoadingProducts(false);
    }
  };

  // API search for tags with debounce - only when user types
  useEffect(() => {
    if (!tagDropdownOpen) return;

    // Load initial tags when dropdown opens (only if tags are empty)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (tags.length === 0 && !tagSearchQuery) {
      fetchTags("");
      return;
    }

    // Only call API when user actually types something
    if (!tagSearchQuery) return;

    const timeoutId = setTimeout(() => {
      fetchTags(tagSearchQuery);
    }, 300);

    return () => {
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagSearchQuery, tagDropdownOpen]);

  if (!open) return null;

  const handleFormChange = (field, value) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
    if (field === 'applyToAll' && value === true) {
      setTagProducts(prev => prev.map(p => ({ ...p, quantity: parseInt(formState.quantity) || 0 })));
    } else if (formState.applyToAll && field === 'quantity') {
      setTagProducts(prev => prev.map(p => ({ ...p, quantity: parseInt(value) || 0 })));
    }
  };

  const handleTagSelect = (tag) => {
    setFormState((prev) => ({ ...prev, selectedTag: tag.identifier || tag.name }));
    setSelectedTagId(tag.id);
    setTagDropdownOpen(false);
    setTagSearchQuery("");
    fetchTagProducts(tag.id);
  };

  const handleProductChange = (productId, value) => {
    setTagProducts(prev => prev.map(p =>
      p.id === productId ? { ...p, quantity: parseInt(value) || 0 } : p
    ));
  };

  const handleRemoveProduct = (productId) => {
    setTagProducts(prev => prev.filter(p => p.id !== productId));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedTagId || tagProducts.length === 0 || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await stockTransferAPI.addTransferItemByTag({
        unique_identifier: formState.selectedTag,
        transfer_id: parseInt(id),
        transfer_number: "",
        note: "",
        products: tagProducts.map(p => ({
          product_id: p.id,
          quantity: p.quantity,
          buying_price: p.buying_price,
          selling_price: p.selling_price,
        })),
      });
      toast.success("Tag added successfully");
      onClose();
      if (onSuccess) await onSuccess();
    } catch (error) {
      console.error("Failed to add tag:", error);
      const errorData = error?.response?.data;
      
      if (errorData?.errors?.stock) {
        const stockErrors = errorData.errors.stock;
        Object.entries(stockErrors).forEach(([productId, stockInfo]) => {
          const product = tagProducts.find(p => p.id === parseInt(productId));
          const productName = product?.name || `Product #${productId}`;
          toast.error(`${productName}: Required ${stockInfo.required}, Available ${stockInfo.available}`);
        });
      } else {
        const message = errorData?.message || errorData?.detail || error?.message || "Failed to add tag";
        toast.error(message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="add-by-tag-modal-overlay"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="add-by-tag-modal" role="dialog" aria-modal="true" ref={dropdownRef}>
        <div className="add-by-tag-modal-header">
          <div>
            <h3 className="add-by-tag-modal-title">Add By Tag</h3>
            <p className="add-by-tag-modal-subtitle">
              Fill in the details below to add tag into the batch.
            </p>
          </div>
        </div>

        <div className="add-by-tag-modal-body">
          <div className="batches-form-group tag-dropdown">
            <label className="batches-form-label">
              Select by Tag <span className="required">*</span>
            </label>
            <div
              className="tag-dropdown-input"
              onClick={() => setTagDropdownOpen((prev) => !prev)}
            >
              <span className={formState.selectedTag ? "selected-value" : "not-selected-value"}>
                {formState.selectedTag || "Select tag"}
              </span>
              <ChevronDown size={16} className="tag-dropdown-arrow" />
            </div>
            {tagDropdownOpen && (
              <div className="tag-dropdown-menu">
                <div className="tag-dropdown-search">
                  <Search size={16} className="tag-search-icon" />
                  <input
                    ref={tagSearchInputRef}
                    type="text"
                    className="tag-search-input"
                    placeholder="Search tags..."
                    value={tagSearchQuery}
                    onChange={(event) => setTagSearchQuery(event.target.value)}
                    onClick={(event) => event.stopPropagation()}
                  />
                </div>
                <div className="tag-dropdown-list">
                  {isLoadingTags ? (
                    <div className="tag-dropdown-item no-results">Loading tags...</div>
                  ) : tags.length > 0 ? (
                    tags.map((tag) => (
                      <div
                        key={tag.id}
                        className="tag-dropdown-item"
                        onClick={() => handleTagSelect(tag)}
                      >
                        {tag.identifier || tag.name}
                      </div>
                    ))
                  ) : (
                    <div className="tag-dropdown-item no-results">No tags found</div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="tag-quantity-row">
            <div className="batches-form-group" style={{ flex: 1 }}>
              <label className="batches-form-label">
                Quantity <span className="required">*</span>
              </label>
              <input
                type="number"
                className="batches-form-input"
                placeholder="E.g. 4000"
                value={formState.quantity}
                onChange={(event) => handleFormChange("quantity", event.target.value)}
              />
            </div>
            <div className="apply-to-all-wrapper">
              <div className="apply-to-all-checkbox">
                <input
                  type="checkbox"
                  id="transferApplyToAll"
                  checked={formState.applyToAll}
                  onChange={(event) => handleFormChange("applyToAll", event.target.checked)}
                />
                <label htmlFor="transferApplyToAll">
                  Apply to All
                  <span className="apply-to-all-info">Entered quantity applied to all</span>
                </label>
              </div>
            </div>
          </div>

          <div className="tag-products-table-container">
            <table className="tag-products-table">
              <thead>
                <tr>
                  <th>PRODUCTS</th>
                  <th>BUYING PRICE</th>
                  <th>SELLING PRICE</th>
                  <th>QUANTITY</th>
                  <th>ACTION</th>
                </tr>
              </thead>
              <tbody>
                {isLoadingProducts ? (
                  <tr>
                    <td colSpan="5" style={{ textAlign: "center" }}>Loading products...</td>
                  </tr>
                ) : tagProducts.length === 0 ? (
                  <tr>
                    <td colSpan="5" style={{ textAlign: "center" }}>No products found</td>
                  </tr>
                ) : (
                  tagProducts.map((product) => (
                    <tr key={product.id}>
                      <td>
                        <div>{product.name}</div>
                        <div style={{ fontSize: "0.8em", color: "#666", marginTop: "4px" }}>
                          {product.ram && `${product.ram}`}
                          {product.ram && product.storage && " | "}
                          {product.storage && `${product.storage}`}
                          {product.variants && product.variants.length > 0 && (
                            <>
                              {(product.ram || product.storage) && " | "}
                              {product.variants.map((variant) => Object.values(variant).join(", ")).join(", ")}
                            </>
                          )}
                          {product.suffix && (
                            <>
                              {(product.ram || product.storage || (product.variants && product.variants.length > 0)) && " | "}
                              {product.suffix}
                            </>
                          )}
                          {product.used_mobile !== undefined && product.used_mobile !== null && (
                            <>
                              {(product.ram || product.storage || (product.variants && product.variants.length > 0) || product.suffix) && " | "}
                              {product.used_mobile === false ? "New" : "Used"}
                            </>
                          )}
                        </div>
                      </td>
                      <td>€{product.buying_price.toFixed(2)}</td>
                      <td>€{product.selling_price.toFixed(2)}</td>
                      <td>
                        <input
                          type="number"
                          className="batches-form-input"
                          value={product.quantity}
                          onChange={(e) => handleProductChange(product.id, e.target.value)}
                          min="0"
                          style={{ width: "100px" }}
                        />
                      </td>
                      <td>
                        <div className="remove-product-btn-container">
                          <button type="button" className="remove-product-btn" onClick={() => handleRemoveProduct(product.id)}>
                            ×
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="add-purchase-modal-footer">
            <button type="button" className="brand-cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="brand-submit-btn-godown"
              style={{ background: primaryColor }}
              disabled={isSubmitting || !selectedTagId || tagProducts.length === 0}
            >
              {isSubmitting ? "Adding..." : "Add Tag"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}